/**
 * LEGACY_COMPAT: Backward compatibility for old config XMLs that lack
 * structured labware metadata (category, mdbId, rows, tipStand, etc.).
 *
 * Old configs encode this info implicitly via wellShape specs, tip_stand specs,
 * stand specs, and labels.  These functions infer the missing fields so that
 * downstream code (classification, deck layout, export) works unchanged.
 *
 * Removal plan: grep -r "LEGACY_COMPAT" src/ → delete this file + call sites.
 */

import type { LabwareType, LabwareSpec } from "./systemConfigParser";

/**
 * Returns true if the config is a legacy (pre-catalog) config that lacks
 * structured labware metadata.  Detection: any labware item without `category`.
 */
export function isLegacyConfig(labware: LabwareType[]): boolean {
  if (labware.length === 0) return false;
  return labware.some((lw) => lw.category == null);
}

/**
 * Mutates labware items in-place, filling in `category`, `rows`, `tipStand`,
 * `adapterType`, `adapterStand`, `deploymentType`, and `deviceRole`
 * from the legacy spec-based encoding.  Already-enriched items are skipped.
 *
 * Note: `tags` are intentionally NOT set here — `deriveLabwareTags()` in
 * deckLayoutUtils derives them at runtime from these structured fields,
 * including additional tags like "tall" that we don't track here.
 *
 * Returns the same array for convenience.
 */
export function enrichLegacyLabware(labware: LabwareType[]): LabwareType[] {
  for (const lw of labware) {
    // Skip items that already have structured metadata
    if (lw.category != null) continue;

    const specs = lw.specifications;
    const wellShape = getSpecValue(specs, "wellShape");
    const dimensions = getSpecValue(specs, "dimensions");

    // Derive rows from "RxC" dimensions string
    if (lw.rows == null && dimensions) {
      const match = String(dimensions).match(/^(\d+)x(\d+)$/);
      if (match) {
        lw.rows = parseInt(match[1], 10);
      }
    }

    // Derive category and category-specific fields from wellShape
    switch (wellShape) {
      case "tip": {
        lw.category = "tip";
        const tipStand = getSpecValue(specs, "tip_stand");
        if (tipStand) {
          lw.tipStand = String(tipStand);
          lw.deploymentType = deriveDeploymentType(String(tipStand));
        }
        break;
      }

      case "adapter": {
        lw.category = "adapter";
        lw.adapterType = deriveAdapterType(lw.label);
        const stand = getSpecValue(specs, "stand");
        if (stand) {
          lw.adapterStand = String(stand);
        }
        break;
      }

      case "waste": {
        lw.category = "device";
        lw.deviceRole = "waste";
        break;
      }

      case "trash": {
        lw.category = "device";
        lw.deviceRole = "trash";
        break;
      }

      default:
        lw.category = "plate";
        break;
    }

    // LEGACY_COMPAT: Look up the correct mdbId from the known legacy typeId map.
    // mdbId is used by the runner as the Composer tip type identifier —
    // defaulting to 0 causes "Unknown tip type '0'" errors.
    if (lw.mdbId == null) {
      lw.mdbId = LEGACY_MDB_IDS[lw.id] ?? 0;
    }
  }

  return labware;
}

// ---------------------------------------------------------------------------
// LEGACY_COMPAT: Known mdb_id values for old-style tip/labware typeIds.
// These come from the Composer database (briefly-runner labware.json).
// Without correct mdbId, the runner gets "Unknown tip type '0'".
// ---------------------------------------------------------------------------

const LEGACY_MDB_IDS: Record<string, number> = {
  // Plates
  "plate-96-deep": 265,
  "plate-96-nunc": 370,
  "plate-96-half-area": 108,
  "plate-384": 6,
  "thermo-96-skirted-0.2ml": 9,
  "thermo-96-0.8ml": 160,
  "falcon-96-flat-2.2ml": 23,
  "fisher-96-deep-2ml": 113,
  "agilent-384-0.2ml": 125,
  "axygen-24-pp-large-volume-reservoir-pyramid-bottom": 371,
  // Reservoirs
  "reservoir-nalgene": 289,
  "reservoir-axygen": 235,
  "agilent-3-column-res": 401,
  "agilent-reservoir-300ml": 236,
  // Tip trays (stand-based)
  "tiptray-1000": 21,
  "tiptray-21": 21,
  "tiptray-60": 16,
  "tiptray-60-filter": 30,
  // Tip racks
  "tiprack-1000": 21,
  // Tip boxes
  "tipbox": 24,
  "tipbox-1000": 21,
  "tipbox-250": 24,
  "tipbox-50": 23,
  "tipbox-250-filter": 26,
  "tipbox-50-filter": 25,
  // Adapters
  "adapter-gripper": 1,
  "adapter-select-13": 1,
  "magnet": 57,
  "magnet-promega": 83,
  "bioshake": 72,
  // Devices
  "waste": 289,
  "trash": 1,
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function getSpecValue(
  specs: LabwareSpec[],
  name: string,
): string | number | undefined {
  const spec = specs.find((s) => s.name === name);
  return spec?.value;
}

/**
 * Derives deploymentType from the tipStand value.
 * "stand105" (or other standNNN) → "tiptray"
 * "tipbox" → "tipbox"
 * "tiprack" → "tiprack"
 */
function deriveDeploymentType(
  tipStand: string,
): "tiptray" | "tiprack" | "tipbox" {
  const lower = tipStand.toLowerCase();
  if (lower === "tipbox") return "tipbox";
  if (lower === "tiprack") return "tiprack";
  // standNNN or any other value defaults to tiptray
  return "tiptray";
}

/**
 * Derives adapterType from the labware label.
 * "8-Channel Adapter" → "8channel"
 * "Gripper Adapter"   → "gripper"
 * "Magnet Adapter"    → "magnet"
 * Fallback: lowercased first word of label
 */
function deriveAdapterType(label: string): string {
  const lower = label.toLowerCase();
  // Match common patterns like "8-channel", "12-channel"
  const channelMatch = lower.match(/(\d+)-?channel/);
  if (channelMatch) return `${channelMatch[1]}channel`;
  // Match single-word descriptors before "adapter"
  const adapterMatch = lower.match(/^(\w+)\s+adapter/);
  if (adapterMatch) return adapterMatch[1];
  return "unknown";
}

